import { useEffect, useMemo, useState } from 'react';
import { Plus, ShieldCheck } from 'lucide-react';
import { P } from '@stockpilot/shared';
import {
  useCreateRoleMutation, usePermissionCatalogueQuery, useRolesQuery, useUpdateRoleMutation,
  type Role,
} from '@/api/endpoints';
import { apiErrorMessage } from '@/api/baseApi';
import { NAVIGATION } from '@/app/navigation';
import {
  Alert, Badge, Button, Card, Checkbox, EmptyState, ErrorState, Input, LoadingState,
  Modal, PageHeader, Table, Td, Textarea, Th,
} from '@/components/ui';
import { Can } from '@/components/Can';

export function RolesPage() {
  const { data, isLoading, isError, error, refetch } = useRolesQuery({ limit: 100 });
  const catalogue = usePermissionCatalogueQuery();
  const [createRole, createState] = useCreateRoleMutation();
  const [updateRole, updateState] = useUpdateRoleMutation();

  const [editing, setEditing] = useState<Role | null>(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ name: '', description: '', permissions: [] as string[] });
  const [tab, setTab] = useState<'pages' | 'permissions'>('pages');

  // Load the selected role into the editor whenever it changes.
  useEffect(() => {
    if (editing) {
      setForm({
        name: editing.name,
        description: editing.description ?? '',
        permissions: [...editing.permissions],
      });
    } else if (creating) {
      setForm({ name: '', description: '', permissions: [] });
    }
  }, [editing, creating]);

  const open = editing !== null || creating;
  const saving = createState.isLoading || updateState.isLoading;
  const saveError = createState.error ?? updateState.error;

  const selected = useMemo(() => new Set(form.permissions), [form.permissions]);

  /**
   * A page is open to a role when the role holds every permission that page
   * requires — the same test the sidebar makes. A page that requires nothing,
   * like the dashboard, is open to everyone.
   */
  const pageIsOpen = (required: readonly string[]): boolean =>
    required.every((code) => selected.has(code));

  /**
   * Turning a page off removes only the permissions no other page still needs.
   *
   * Permissions are shared: SALE_VIEW opens Sales and Quotations both. Stripping
   * it because one page was closed would silently shut the other, which is
   * exactly the kind of surprise an access screen must not spring.
   */
  const togglePage = (route: string, required: readonly string[], open: boolean): void => {
    setForm((current) => {
      if (open) {
        return { ...current, permissions: [...new Set([...current.permissions, ...required])] };
      }

      const stillNeeded = new Set<string>();
      for (const section of NAVIGATION) {
        for (const item of section.items) {
          const codes = (item.permissions ?? []) as readonly string[];
          if (codes.length === 0) continue;
          // A page's identity is its route, not its permissions: Sales and
          // Quotations both read SALE_VIEW, and closing one must leave the other
          // exactly as it was.
          if (item.to === route) continue;
          if (codes.every((code) => current.permissions.includes(code))) {
            for (const code of codes) stillNeeded.add(code);
          }
        }
      }

      return {
        ...current,
        permissions: current.permissions.filter(
          (code) => !required.includes(code) || stillNeeded.has(code),
        ),
      };
    });
  };

  const togglePermission = (code: string): void =>
    setForm((current) => ({
      ...current,
      permissions: current.permissions.includes(code)
        ? current.permissions.filter((entry) => entry !== code)
        : [...current.permissions, code],
    }));

  const toggleGroup = (codes: string[], checked: boolean): void =>
    setForm((current) => ({
      ...current,
      permissions: checked
        ? [...new Set([...current.permissions, ...codes])]
        : current.permissions.filter((code) => !codes.includes(code)),
    }));

  const close = (): void => {
    setEditing(null);
    setCreating(false);
  };

  const save = async (): Promise<void> => {
    const result = editing
      ? await updateRole({
          id: editing.id,
          body: { name: form.name, description: form.description, permissions: form.permissions },
        })
          .unwrap()
          .catch(() => null)
      : await createRole({
          name: form.name,
          description: form.description || undefined,
          permissions: form.permissions,
        })
          .unwrap()
          .catch(() => null);

    if (result) close();
  };

  return (
    <>
      <PageHeader
        title="Roles & permissions"
        description="Roles are yours to shape. The API enforces every permission listed here."
        action={
          <Can permission={P.ROLE_MANAGE}>
            <Button icon={Plus} onClick={() => setCreating(true)}>New role</Button>
          </Can>
        }
      />

      <Card>
        {isLoading ? (
          <LoadingState />
        ) : isError ? (
          <ErrorState message={apiErrorMessage(error)} onRetry={() => void refetch()} />
        ) : data && data.items.length === 0 ? (
          <EmptyState icon={ShieldCheck} title="No roles defined" />
        ) : (
          <Table>
            <thead>
              <tr>
                <Th>Role</Th>
                <Th numeric>Permissions</Th>
                <Th numeric>Members</Th>
                <Th />
              </tr>
            </thead>
            <tbody>
              {data?.items.map((role) => (
                <tr key={role.id} className="hover:bg-sunken">
                  <Td>
                    <div className="flex items-center gap-2">
                      <p className="font-medium">{role.name}</p>
                      {role.isSystem && <Badge tone="neutral">Built-in</Badge>}
                    </div>
                    {role.description && <p className="text-xs text-muted">{role.description}</p>}
                  </Td>
                  <Td numeric className="text-secondary">
                    {role.isOwnerRole ? 'All, always' : role.permissions.length}
                  </Td>
                  <Td numeric className="text-secondary">{role.memberCount}</Td>
                  <Td className="text-right">
                    <Can permission={P.ROLE_MANAGE}>
                      <Button
                        size="sm"
                        variant="ghost"
                        disabled={role.isOwnerRole}
                        title={role.isOwnerRole ? 'The Owner role always holds every permission' : undefined}
                        onClick={() => setEditing(role)}
                      >
                        Edit
                      </Button>
                    </Can>
                  </Td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card>

      <Modal
        open={open}
        onClose={close}
        size="lg"
        title={editing ? `Edit ${editing.name}` : 'New role'}
        description="Tick the actions this role may perform."
        footer={
          <>
            <Button variant="outline" onClick={close} disabled={saving}>Cancel</Button>
            <Button onClick={() => void save()} loading={saving} disabled={!form.name.trim()}>
              {editing ? 'Save changes' : 'Create role'}
            </Button>
          </>
        }
      >
        <div className="space-y-5">
          {saveError && <Alert tone="danger">{apiErrorMessage(saveError)}</Alert>}

          <Input
            label="Role name"
            required
            value={form.name}
            onChange={(event) => setForm({ ...form, name: event.target.value })}
          />
          <Textarea
            label="Description"
            value={form.description}
            onChange={(event) => setForm({ ...form, description: event.target.value })}
          />

          <div>
            <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
              {/* Administrators think in screens; the permission codes underneath
                  are still there for anyone who needs that precision. */}
              <div className="inline-flex rounded-lg border border-subtle p-0.5">
                <button
                  type="button"
                  onClick={() => setTab('pages')}
                  className={`rounded-md px-3 py-1.5 text-sm font-medium ${
                    tab === 'pages' ? 'bg-sunken' : 'text-secondary'
                  }`}
                >
                  Pages
                </button>
                <button
                  type="button"
                  onClick={() => setTab('permissions')}
                  className={`rounded-md px-3 py-1.5 text-sm font-medium ${
                    tab === 'permissions' ? 'bg-sunken' : 'text-secondary'
                  }`}
                >
                  Permissions
                </button>
              </div>
              <p className="text-sm text-muted">{form.permissions.length} permissions selected</p>
            </div>

            {tab === 'pages' ? (
              <div className="space-y-4">
                <p className="text-sm text-muted">
                  What this role can open. Switching a page off removes the permissions behind
                  it, except those another page it still has also needs.
                </p>

                {NAVIGATION.map((section) => (
                  <fieldset key={section.label} className="rounded-lg border border-subtle p-3">
                    <legend className="px-1 text-sm font-medium">{section.label}</legend>
                    <div className="mt-2 grid gap-2 sm:grid-cols-2">
                      {section.items.map((item) => {
                        const required = (item.permissions ?? []) as readonly string[];

                        if (required.length === 0) {
                          return (
                            <p key={item.to} className="text-sm text-muted">
                              {item.label} — open to everyone
                            </p>
                          );
                        }

                        return (
                          <Checkbox
                            key={item.to}
                            label={item.label}
                            checked={pageIsOpen(required)}
                            onChange={(event) =>
                              togglePage(item.to, required, event.target.checked)
                            }
                          />
                        );
                      })}
                    </div>
                  </fieldset>
                ))}
              </div>
            ) : catalogue.isLoading ? (
              <LoadingState label="Loading permissions…" />
            ) : (
              <div className="space-y-4">
                {catalogue.data?.map((group) => {
                  const codes = group.permissions.map((permission) => permission.code);
                  const allSelected = codes.every((code) => selected.has(code));

                  return (
                    <fieldset key={group.group} className="rounded-lg border border-subtle p-3">
                      <legend className="px-1">
                        <Checkbox
                          label={group.group}
                          checked={allSelected}
                          onChange={(event) => toggleGroup(codes, event.target.checked)}
                        />
                      </legend>
                      <div className="mt-2 grid gap-2 sm:grid-cols-2">
                        {group.permissions.map((permission) => (
                          <Checkbox
                            key={permission.code}
                            label={permission.description}
                            checked={selected.has(permission.code)}
                            onChange={() => togglePermission(permission.code)}
                          />
                        ))}
                      </div>
                    </fieldset>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </Modal>
    </>
  );
}
